import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Loader2 } from 'lucide-react';
import { parseM3U8 } from '../../utils/m3u8Parser';
import { setError, appendChannels, setLoading } from '../../store/slices/playlistSlice';
import { useTranslation } from 'react-i18next';
import { analyticsService } from '../../services/analyticsService';
import { RootState } from '../../store';
import logger from '../../utils/logger';
import { useFirestore } from '../../hooks/useFirestore';
import { usePlaylistAutoSave } from '../../hooks/usePlaylistAutoSave';
import { usePlaylistDetection } from '../../contexts/PlaylistDetectionContext';
import clsx from 'clsx';

export const UrlPlaylistImport: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [localLoading, setLocalLoading] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const { t } = useTranslation();
  const { savePlaylist } = useFirestore();
  const { autoSave } = usePlaylistAutoSave();
  const urlInputRef = useRef<HTMLInputElement>(null);
  const { isPlaylistDetected } = usePlaylistDetection();
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [processedUrls] = useState(new Set<string>());
  
  // Memoize selectors to prevent unnecessary re-renders
  const { user } = useSelector((state: RootState) => state.auth, (prev, next) => 
    prev?.user?.uid === next?.user?.uid
  );
  const { isLoading: isPlaylistLoading, channels } = useSelector(
    (state: RootState) => ({
      isLoading: state.playlist.isLoading,
      channels: state.playlist.channels
    }),
    (prev, next) => prev.isLoading === next.isLoading && prev.channels.length === next.channels.length
  );
  
  const isLoggedIn = useMemo(() => user !== null, [user]);

  // Memoize the import playlist function
  const importPlaylist = useCallback(async () => {
    const queryParams = new URLSearchParams(location.search);
    const encodedPlaylistUrl = queryParams.get('playlist');
    const source = queryParams.get('source') || 'unknown';
    const tab = queryParams.get('tab');
    
    if (!encodedPlaylistUrl || processedUrls.has(encodedPlaylistUrl)) {
      setLocalLoading(false);
      return;
    }
    
    processedUrls.add(encodedPlaylistUrl);
    
    // Only set loading to true if we have a playlist URL
    setLocalLoading(true);
    dispatch(setLoading(true));
    
    try {
      const playlistUrl = decodeURIComponent(encodedPlaylistUrl);
      logger.debug('UrlPlaylistImport - Importing playlist from URL:', playlistUrl);
      
      // Validate the URL
      try {
        new URL(playlistUrl);
      } catch (e) {
        dispatch(setError({ 
          message: t('errors.invalidUrl', 'Please provide a valid URL'),
          type: 'error' 
        }));
        setLocalLoading(false);
        dispatch(setLoading(false));
        return;
      }

      // Fetch playlist content
      const response = await fetch(playlistUrl, {
        mode: 'cors',
        credentials: 'omit',
        headers: {
          'Accept': 'text/plain, application/x-mpegurl, application/vnd.apple.mpegurl',
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch playlist');
      }

      const content = await response.text();
      logger.debug('UrlPlaylistImport - Parsing M3U8 content, content length:', content.length);
      const parsedChannels = parseM3U8(content);
      logger.debug('UrlPlaylistImport - Parsed channels from URL:', parsedChannels.length);
      
      if (parsedChannels.length === 0) {
        dispatch(setError({ 
          message: t('errors.noChannels', 'No channels found in the playlist'),
          type: 'error' 
        }));
        setLocalLoading(false);
        dispatch(setLoading(false));
        return;
      }

      // Create a copy of existing channels to ensure we have the right data for saving
      const existingChannels = [...channels];
      logger.debug('UrlPlaylistImport - Current channels in Redux state before update:', existingChannels.length);

      // Add channels to playlist
      dispatch(appendChannels(parsedChannels));
      logger.debug('UrlPlaylistImport - Dispatched appendChannels action, channels to add:', parsedChannels.length);
      
      // Auto-save to Firestore if user is authenticated
      if (user?.uid) {
        setIsAutoSaving(true);
        try {
          logger.debug('UrlPlaylistImport - Starting auto-save process');
          
          // Wait for state to update - ensure state propagation
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Calculate what the new state should be (for logging)
          const expectedChannelCount = existingChannels.length + parsedChannels.length;
          logger.debug('UrlPlaylistImport - Expected channel count after update:', expectedChannelCount);
          logger.debug('UrlPlaylistImport - Actual channel count in Redux before save:', channels.length);
          
          // Save all channels including the newly imported ones
          if (channels.length < expectedChannelCount) {
            logger.debug('Redux state not yet updated, using direct save with combined channels');
            const combinedChannels = [...existingChannels, ...parsedChannels];
            await savePlaylist(user.uid, combinedChannels);
          } else {
            logger.debug('Using direct save with updated Redux state');
            await autoSave();
          }
          
          logger.debug('UrlPlaylistImport - Auto-save completed successfully');
          
          // Show success message with save confirmation
          dispatch(setError({ 
            message: t('playlist.importAndSaveSuccess', 'Successfully imported and saved {{count}} channels', { count: parsedChannels.length }),
            type: 'success' 
          }));
        } catch (error) {
          logger.error('UrlPlaylistImport - Failed to auto-save playlist:', error);
          dispatch(setError({ 
            message: t('errors.autoSaveFailed', 'Channels imported but failed to save automatically. Please try saving manually.'),
            type: 'error' 
          }));
        } finally {
          setIsAutoSaving(false);
        }
      } else {
        // Show regular success message without save confirmation
        dispatch(setError({ 
          message: t('playlist.importSuccess', 'Successfully imported {{count}} channels', { count: parsedChannels.length }),
          type: 'success' 
        }));
      }

      // Track analytics event
      analyticsService.trackEvent(analyticsService.events.PLAYLIST_IMPORTED, {
        source: source,
        channelCount: parsedChannels.length,
        autoSaved: isLoggedIn
      });

      // Set the active tab to 'channels' in localStorage
      // If tab parameter is provided, use that (should be 'channels')
      if (tab) {
        localStorage.setItem('activePlaylistTab', tab);
      } else {
        localStorage.setItem('activePlaylistTab', 'channels');
      }

      // Clear the query parameter
      navigate('/', { replace: true });
    } catch (error) {
      logger.error('Error importing playlist from URL:', error);
      dispatch(setError({ 
        message: t('errors.urlImportError', 'Failed to import playlist from URL'),
        type: 'error' 
      }));
    } finally {
      setLocalLoading(false);
      dispatch(setLoading(false));
    }
  }, [location.search, dispatch, navigate, t, user?.uid, channels, processedUrls]);

  // Effect for playlist detection
  useEffect(() => {
    setIsHighlighted(isPlaylistDetected);
  }, [isPlaylistDetected]);

  // Effect for importing playlist
  useEffect(() => {
    if (location.search) {
      importPlaylist();
    }
  }, [location.search, importPlaylist]);

  // Debug render only in development
  if (process.env.NODE_ENV === 'development') {
    useEffect(() => {
      logger.debug('UrlPlaylistImport - Component rendered', {
        locationSearch: location.search,
        isLoggedIn,
        channelsCount: channels.length,
        isLoading: isPlaylistLoading
      });
    });
  }

  // Only show loading if we're handling a URL import (localLoading) 
  // and not if the playlist is being loaded from Firestore (isPlaylistLoading)
  const shouldShowLoading = localLoading && !isPlaylistLoading;
  
  // If loading, show a loading indicator
  if (shouldShowLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {t('playlist.importing', 'Importing Playlist...')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {t('playlist.importingWait', 'Please wait while we import your playlist')}
        </p>
        {isAutoSaving && (
          <p className="text-sm text-blue-500 dark:text-blue-400 mt-2">
            {t('playlist.autoSaving', 'Saving changes...')}
          </p>
        )}
      </div>
    );
  }

  // If not loading, don't render anything
  return null;
};

const UrlPlaylistImportForm: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const urlInputRef = useRef<HTMLInputElement>(null);
  const { isPlaylistDetected } = usePlaylistDetection();
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const { channels } = useSelector((state: RootState) => state.playlist);
  const { user } = useSelector((state: RootState) => state.auth);
  const { savePlaylist } = useFirestore();
  const { autoSave } = usePlaylistAutoSave();

  useEffect(() => {
    setIsHighlighted(isPlaylistDetected);
  }, [isPlaylistDetected]);

  const handleUrlImport = async () => {
    if (!urlInputRef.current?.value.trim()) {
      dispatch(setError({ message: t('errors.urlRequired', 'Please enter a URL'), type: 'error' }));
      return;
    }

    try {
      const url = new URL(urlInputRef.current.value.trim());
      if (url.protocol !== 'https:') {
        dispatch(setError({ message: t('errors.httpsRequired', 'Only HTTPS URLs are supported for security reasons'), type: 'error' }));
        return;
      }

      dispatch(setLoading(true));
      try {
        logger.debug('Fetching playlist from URL:', url.toString());
        const response = await fetch(url.toString(), {
          mode: 'cors',
          credentials: 'omit',
          headers: {
            'Accept': 'text/plain, application/x-mpegurl, application/vnd.apple.mpegurl',
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch playlist');
        }

        const content = await response.text();
        logger.debug('Parsing M3U8 content, content length:', content.length);
        const parsedChannels = parseM3U8(content);
        logger.debug('Parsed channels from URL:', parsedChannels.length);
        
        if (parsedChannels.length === 0) {
          dispatch(setError({ message: t('errors.noChannels', 'No channels found in the playlist'), type: 'error' }));
          dispatch(setLoading(false));
          return;
        }

        // Count HTTP and HTTPS channels
        const httpsChannels = parsedChannels.filter((channel: any) => {
          try {
            const urlObj = new URL(channel.url);
            return urlObj.protocol === 'https:';
          } catch {
            return false;
          }
        });
        const httpChannels = parsedChannels.filter((channel: any) => {
          try {
            const urlObj = new URL(channel.url);
            return urlObj.protocol === 'http:';
          } catch {
            return false;
          }
        });

        // Create a copy of existing channels to ensure we have the right data for saving
        const existingChannels = [...channels];
        logger.debug('Current channels in Redux state before update:', existingChannels.length);

        // Add all channels to store
        dispatch(appendChannels(parsedChannels));
        logger.debug('Dispatched appendChannels action, channels to add:', parsedChannels.length);

        // Auto-save for both authenticated and non-authenticated users
        setIsAutoSaving(true);
        try {
          logger.debug('Starting auto-save process');
          
          // Wait for state to update - ensure state propagation
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Calculate what the new state should be (for logging)
          const expectedChannelCount = existingChannels.length + parsedChannels.length;
          logger.debug('Expected channel count after update:', expectedChannelCount);
          logger.debug('Actual channel count in Redux before save:', channels.length);
          
          // Save all channels including the newly imported ones
          if (channels.length < expectedChannelCount) {
            logger.debug('Redux state not yet updated, using direct save with combined channels');
            const combinedChannels = [...existingChannels, ...parsedChannels];
            if (user?.uid) {
              await savePlaylist(user.uid, combinedChannels);
            }
          } else {
            logger.debug('Using direct save with updated Redux state');
            await autoSave();
          }
          
          logger.debug('Auto-save completed successfully');
          
          dispatch(setError({ 
            message: t('playlist.importAndSaveSuccessWithHttp', 'Successfully imported and saved {{total}} channels ({{https}} HTTPS, {{http}} HTTP)', { 
              total: parsedChannels.length,
              https: httpsChannels.length,
              http: httpChannels.length
            }),
            type: 'success' 
          }));
        } catch (error) {
          logger.error('Failed to auto-save playlist:', error);
          dispatch(setError({ 
            message: t('errors.autoSaveFailed', 'Channels imported but failed to save automatically. Please try saving manually.'),
            type: 'error' 
          }));
        } finally {
          setIsAutoSaving(false);
        }
        
        urlInputRef.current.value = '';
        localStorage.setItem('activePlaylistTab', 'channels');
      } catch (error) {
        logger.error('Error importing playlist from URL:', error);
        dispatch(setError({ message: t('errors.urlImportError', 'Failed to import playlist from URL'), type: 'error' }));
      } finally {
        dispatch(setLoading(false));
      }
    } catch (error) {
      dispatch(setError({ message: t('errors.invalidUrl', 'Please enter a valid URL'), type: 'error' }));
    }
  };

  return (
    <div className="mb-8">
      <div className="flex flex-col sm:flex-row gap-4">
        <input
          ref={urlInputRef}
          type="url"
          placeholder={t('playlist.urlPlaceholder', 'https://example.com/playlist.m3u8')}
          className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary dark:bg-gray-700 dark:text-white"
        />
        <button
          onClick={handleUrlImport}
          className={clsx(
            "px-6 py-2 text-white rounded-lg shadow-sm",
            "bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary",
            "transition-all duration-200",
            {
              'animate-pulse ring-2 ring-primary ring-offset-2': isHighlighted,
            }
          )}
        >
          {t('playlist.importPlaylist', 'Import Playlist')}
        </button>
      </div>
    </div>
  );
}; 